// Canaux clients — the pieces shared by the three widget pages (chatbot,
// ticket form, tracking page): brand picker, brand colour picker with the
// contrast check, colour / image fields, embed code block, preview card,
// « À vérifier » card and the save bar with its dirty state. Every piece
// composes the shared .u-* components; ChannelsKit.css lays them out
// under .chan-v2__*. The previews themselves (ChatbotPreview,
// TicketFormPreview, TrackingPreview) stay the widgets' faithful mirrors.

import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Check, ChevronRight, Copy, Eye, ImagePlus, LoaderCircle, Plus, TriangleAlert, Undo2, X,
} from 'lucide-react';
import { Badge, Card, ConfirmDialog, Segmented, Switch } from '../design/ui';
import { formatShortDate } from '../utils/dates';
import './ChannelsKit.css';

const cx = (...names) => names.filter(Boolean).join(' ');

// Curated brand palettes: one click sets the brand colour (same order as v2).
export const THEME_PRESETS = ['#C47A4A', '#3FBDB4', '#4A6CC4', '#1F2937', '#B04A5A', '#3E7D52'];

export const isHex = (value) => /^#[0-9a-fA-F]{6}$/.test(value || '');

// WCAG contrast of a brand colour against the text colour the widgets pick
// for it (black or white, YIQ rule — same as the widgets).
export const contrastRatio = (hex) => {
  if (!isHex(hex)) return null;
  const channel = (v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  const lum = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  const textLum = (r * 299 + g * 587 + b * 114) / 1000 >= 128 ? 0 : 1;
  return (Math.max(lum, textLum) + 0.05) / (Math.min(lum, textLum) + 0.05);
};

export const formatRatio = (ratio, lang) => (ratio === null || ratio === undefined
  ? '—'
  : ratio.toLocaleString(lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 }));

export const formatPercent = (value, lang) => new Intl.NumberFormat(lang, { style: 'percent', maximumFractionDigits: 0 })
  .format((Number(value) || 0) / 100);

// « 28 oct. » (« 28 oct. 2025 » another year) — the shared short format.
export const formatDay = (value, lang) => formatShortDate(value, lang);

export const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

// Tickets one channel received over the last 30 days, from
// GET /api/statistics/overview → channels (statistics_service
// .get_tickets_per_channel: tenant-scoped, Test channel and demo excluded).
// Chatbot tickets live on the « Website » channel, form tickets on
// « Ticketing » (Backend channel_service). A channel absent from a non-empty
// answer received no ticket; an empty answer proves nothing (a failed
// sub-aggregation answers the same), so it yields null — never a fake 0.
export const channelTraffic = (overview, channelName) => {
  const block = overview?.channels;
  if (!block || !Array.isArray(block.channels) || !(Number(block.total_tickets) > 0)) return null;
  const row = block.channels.find((item) => item.channel_name === channelName);
  const count = row ? Number(row.ticket_count) || 0 : 0;
  const total = Number(block.total_tickets);
  return {
    count,
    total,
    share: (count / total) * 100,
    aiResolved: row ? Number(row.ai_resolved) || 0 : 0,
    aiRate: count ? ((Number(row.ai_resolved) || 0) / count) * 100 : null,
  };
};

// One read of the account's 30-day channel mix (the page shows it, nothing
// else depends on it): { status: 'loading' | 'ready' | 'error', overview }.
export const useChannelOverview = () => {
  const [state, setState] = useState({ status: 'loading', overview: null });
  useEffect(() => {
    let alive = true;
    axios.get('/api/statistics/overview?days=30', { headers: authHeaders() })
      .then((res) => { if (alive) setState({ status: 'ready', overview: res.data }); })
      .catch(() => { if (alive) setState({ status: 'error', overview: null }); });
    return () => { alive = false; };
  }, []);
  return state;
};

export const copyText = async (text) => {
  if (!text || !navigator.clipboard?.writeText) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    return false;
  }
};

export const scrollToId = (id) => {
  const node = document.getElementById(id);
  if (node?.scrollIntoView) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// ---------------------------------------------------------------- pickers

// One tile per brand (and the default): which one the page edits, and its
// state at a glance. entries: [{ key, name, meta, color, swatch, badge, create }]
// (swatch: false when the page knows no colour per brand).
export const BrandPicker = ({ label, entries, selectedKey, onSelect }) => (
  <div className="chan-v2__brands" role="radiogroup" aria-label={label}>
    {entries.map((entry) => {
      const selected = entry.key === selectedKey;
      return (
        <button
          key={entry.key || '__default__'}
          type="button"
          role="radio"
          aria-checked={selected}
          className={cx('chan-v2__brand', selected && 'is-selected', entry.create && 'chan-v2__brand--create')}
          onClick={() => onSelect(entry)}
        >
          {entry.create && <Plus className="chan-v2__brand-plus" strokeWidth={1.8} aria-hidden="true" />}
          {!entry.create && entry.swatch !== false && (
            <span
              className={cx('chan-v2__brand-swatch', !entry.color && 'is-empty')}
              // the tenant's own brand colour (data), shown as a swatch
              style={entry.color ? { backgroundColor: entry.color } : undefined}
              aria-hidden="true"
            />
          )}
          <span className="chan-v2__brand-text">
            <strong>{entry.name}</strong>
            {entry.meta && <small>{entry.meta}</small>}
          </span>
          {entry.badge}
        </button>
      );
    })}
  </div>
);

// Preset palettes + a custom colour + the contrast verdict of the result.
export const BrandColorPicker = ({ value, onPick, lang, labels }) => {
  const ratio = contrastRatio(value);
  const ok = ratio !== null && ratio >= 4.5;
  const current = String(value || '').toUpperCase();
  return (
    <div className="u-field">
      <span className="u-label">{labels.title}</span>
      <div className="chan-v2__presets">
        {THEME_PRESETS.map((color) => (
          <button
            key={color}
            type="button"
            className={cx('chan-v2__preset', current === color && 'is-active')}
            style={{ backgroundColor: color }}
            onClick={() => onPick(color)}
            aria-label={color}
            aria-pressed={current === color}
            title={color}
          />
        ))}
        <label className={cx('chan-v2__preset-custom', isHex(value) && !THEME_PRESETS.includes(current) && 'is-active')} title={labels.custom}>
          <input type="color" value={isHex(value) ? value : '#000000'} onChange={(e) => onPick(e.target.value)} aria-label={labels.custom} />
          <Plus strokeWidth={2} aria-hidden="true" />
        </label>
        <span className="u-code chan-v2__preset-value">{value || '—'}</span>
      </div>
      <p className={cx('chan-v2__contrast', ok ? 'is-ok' : 'is-warn')}>
        {ok ? <Check strokeWidth={2} aria-hidden="true" /> : <TriangleAlert strokeWidth={2} aria-hidden="true" />}
        {ok ? labels.ok(formatRatio(ratio, lang)) : labels.warn(formatRatio(ratio, lang))}
      </p>
    </div>
  );
};

// A colour: native picker + its hex value (empty = automatic when allowed).
export const ColorField = ({ id, label, value, fallback = '#000000', placeholder, onChange, help }) => (
  <div className="u-field">
    <label className="u-label" htmlFor={id}>{label}</label>
    <div className="chan-v2__color">
      <input
        type="color"
        className="chan-v2__color-swatch"
        value={isHex(value) ? value : (isHex(fallback) ? fallback : '#000000')}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
      />
      <input
        id={id}
        type="text"
        className="u-input chan-v2__color-hex"
        value={value || ''}
        placeholder={placeholder}
        maxLength={7}
        spellCheck={false}
        aria-invalid={Boolean(value) && !isHex(value)}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
    {help && <p className="u-help">{help}</p>}
  </div>
);

// Slider + its value, for bounded numbers (bubble size, delay, radius).
export const RangeField = ({ id, label, value, min, max, unit, onChange, withNumber }) => (
  <div className="u-field">
    <label className="u-label" htmlFor={id}>
      {label}
      <span className="chan-v2__range-value u-num">{value}{unit ? ` ${unit}` : ''}</span>
    </label>
    <div className="chan-v2__range">
      <input id={id} type="range" min={min} max={max} value={value} onChange={(e) => onChange(e.target.value)} />
      {withNumber && (
        <input
          type="number"
          className="u-input chan-v2__range-number"
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
        />
      )}
    </div>
  </div>
);

// Upload an image (PNG, JPEG, WebP) by click or drop; the server sniffs and
// re-encodes it. Removing only clears the draft (saved on « Enregistrer »).
export const ImageDrop = ({
  label, imageUrl, fallback, hint, uploading, uploadingLabel, onFile, onRemove, removeLabel, wide,
}) => {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const open = () => inputRef.current?.click();
  return (
    <div className="u-field">
      <span className="u-label">{label}</span>
      <div className="chan-v2__drop-row">
        <div
          role="button"
          tabIndex={0}
          aria-label={label}
          className={cx('chan-v2__drop', over && 'is-over', wide && 'chan-v2__drop--wide')}
          onClick={open}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); onFile(e.dataTransfer.files?.[0]); }}
        >
          <span className="chan-v2__drop-thumb">
            {imageUrl ? <img src={imageUrl} alt="" /> : fallback}
          </span>
          <span className="chan-v2__drop-text">
            {uploading
              ? <><LoaderCircle className="chan-v2__spin" strokeWidth={1.8} aria-hidden="true" />{uploadingLabel}</>
              : <><ImagePlus strokeWidth={1.8} aria-hidden="true" />{hint}</>}
          </span>
          <input
            ref={inputRef}
            type="file"
            accept=".png,.jpg,.jpeg,.webp"
            hidden
            onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ''; }}
          />
        </div>
        {imageUrl && (
          <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={onRemove}>
            <X aria-hidden="true" />
            {removeLabel}
          </button>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- install

// The script tag to paste, in a copyable block.
export const EmbedCode = ({ code, loading, error, hint, copyLabel, copiedLabel, loadingLabel }) => {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);
  return (
    <div className="chan-v2__embed">
      {hint && <p className="u-help">{hint}</p>}
      {error ? (
        <p className="u-error-text" role="alert">{error}</p>
      ) : loading || !code ? (
        <span className="u-skeleton chan-v2__embed-skeleton" aria-label={loadingLabel} />
      ) : (
        <div className="chan-v2__code">
          <pre><code>{code}</code></pre>
          <button
            type="button"
            className="u-btn u-btn--secondary u-btn--sm chan-v2__code-copy"
            onClick={async () => setCopied(await copyText(code))}
          >
            {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
            {copied ? copiedLabel : copyLabel}
          </button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- frame

// What needs a person: items [{ key, tone, title, detail, action }].
export const AttentionCard = ({ items, title, criticalLabel, watchLabel }) => {
  if (!items.length) return null;
  return (
    <Card
      icon={TriangleAlert}
      iconTone="warning"
      title={title}
      badge={<Badge tone="warning">{items.length}</Badge>}
      flush
      className="chan-v2__attention"
    >
      <ul className="u-list">
        {items.map((item) => (
          <li className="u-row" key={item.key}>
            <Badge tone={item.tone} dot>{item.tone === 'danger' ? criticalLabel : watchLabel}</Badge>
            <div className="u-row__main">
              <div className="u-row__title">{item.title}</div>
              {item.detail && <div className="u-row__meta u-row__meta--wrap">{item.detail}</div>}
            </div>
            {item.action && (
              <div className="u-row__side">
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={item.action.onClick} disabled={item.action.disabled}>
                  {item.action.label}
                  <ChevronRight aria-hidden="true" />
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
};

// The live preview: its views on a segmented control, the widget itself
// (styled by the tenant's brand settings, never by the app), a note.
export const PreviewCard = ({ title, badge, modes, mode, onMode, modesLabel, note, children }) => (
  <aside className="chan-v2__preview">
    <Card icon={Eye} title={title} badge={badge}>
      {modes && (
        <div className="chan-v2__preview-modes">
          <Segmented options={modes} value={mode} onChange={onMode} label={modesLabel} />
        </div>
      )}
      {children}
      {note && <p className="u-help chan-v2__preview-note">{note}</p>}
    </Card>
  </aside>
);

// Sticky footer of the settings column: saved / unsaved state, revert, save.
export const SaveBar = ({
  dirty, saving, canSave, onSave, onReset, labels, note,
}) => (
  <div className={cx('u-card chan-v2__savebar', dirty && 'is-dirty')} role="region" aria-label={labels.region}>
    <span className="chan-v2__savebar-state">
      {note && <span className="chan-v2__savebar-note">{note}</span>}
      {dirty
        ? <Badge tone="warning" dot>{labels.dirty}</Badge>
        : <span className="chan-v2__saved"><Check strokeWidth={2} aria-hidden="true" />{labels.clean}</span>}
    </span>
    {dirty && onReset && (
      <button type="button" className="u-btn u-btn--secondary" onClick={onReset} disabled={saving}>
        {labels.reset}
      </button>
    )}
    <button type="button" className="u-btn u-btn--primary" onClick={onSave} disabled={saving || !canSave}>
      {saving ? labels.saving : labels.save}
    </button>
  </div>
);

// A setting that is on or off, with what it does underneath.
export const SwitchRow = ({ id, label, help, checked, onChange, disabled }) => (
  <div className="chan-v2__switch-row">
    <div className="chan-v2__switch-text">
      <label className="u-label" htmlFor={id}>{label}</label>
      {help && <p className="u-help">{help}</p>}
    </div>
    <Switch id={id} checked={Boolean(checked)} onChange={onChange} disabled={disabled} />
  </div>
);

// Leaving a draft (another brand, another chatbot) asks first.
export const DiscardDialog = ({ t, name, onConfirm, onCancel }) => (
  <ConfirmDialog
    title={t('channelsUi.discardTitle', 'Abandonner les modifications ?')}
    message={t('channelsUi.discardMessage', 'Les modifications de « {{name}} » ne sont pas enregistrées : elles seront perdues.', { name })}
    confirmLabel={t('channelsUi.discard', 'Abandonner')}
    cancelLabel={t('channelsUi.keepEditing', 'Continuer l’édition')}
    icon={Undo2}
    onConfirm={onConfirm}
    onCancel={onCancel}
  />
);

// Words every widget page shares (fr defaults; keys in channelsUi).
export const kitLabels = (t) => ({
  attention: t('channelsUi.attention', 'À vérifier'),
  critical: t('channelsUi.critical', 'Bloquant'),
  watch: t('channelsUi.watch', 'À surveiller'),
  save: {
    region: t('channelsUi.save.region', 'Enregistrement'),
    dirty: t('channelsUi.save.dirty', 'Modifications non enregistrées'),
    clean: t('channelsUi.save.clean', 'Tout est enregistré'),
    reset: t('channelsUi.save.reset', 'Annuler les modifications'),
    save: t('common.save', 'Enregistrer'),
    saving: t('common.saving', 'Enregistrement...'),
  },
  embed: {
    copy: t('channelsUi.embed.copy', 'Copier'),
    copied: t('channelsUi.embed.copied', 'Copié'),
    loading: t('channelsUi.embed.loading', 'Génération du code…'),
    copyCode: t('channelsUi.embed.copyCode', 'Copier le code d’intégration'),
    copiedToast: t('channelsUi.embed.copiedToast', 'Code d’intégration copié : collez-le dans le HTML de votre site.'),
    copyFailed: t('channelsUi.embed.copyFailed', 'La copie a échoué : sélectionnez le code dans la carte « Installation » et copiez-le.'),
  },
  preview: t('channelsUi.preview', 'Aperçu'),
  hiddenOnSite: t('channelsUi.hiddenOnSite', 'Masqué sur le site'),
  visible: t('channelsUi.visible', 'Visible'),
  hidden: t('channelsUi.hidden', 'Masqué'),
  notConfigured: t('channelsUi.notConfigured', 'Non configuré'),
  brands: t('channelsUi.brands', 'Marques'),
  moreActions: t('channelsUi.moreActions', 'Plus d’actions'),
  close: t('common.close', 'Fermer'),
  cancel: t('common.cancel', 'Annuler'),
});

export const KpiSkeletons = ({ count = 3 }) => (
  <div className="u-kpi-row">
    {Array.from({ length: count }, (_, i) => <span key={i} className="u-skeleton chan-v2__kpi-skeleton" />)}
  </div>
);

export const PageSkeleton = ({ header }) => (
  <>
    {header}
    <div className="u-well">
      <KpiSkeletons />
      <div className="chan-v2__layout">
        <span className="u-skeleton chan-v2__card-skeleton" />
        <span className="u-skeleton chan-v2__card-skeleton" />
      </div>
    </div>
  </>
);
